import { Eye, EyeOff, Sparkles } from "lucide-react";

const MODES = [
  { value: "auto", label: "auto", description: "follow the pipeline stage. visible once the piece reaches listing & marketing." },
  { value: "force_published", label: "force published", description: "always public, no matter the stage. use to fast-track a piece live." },
  { value: "force_hidden", label: "force hidden", description: "never public, no matter the stage. use to pull a piece from the shop temporarily." },
];

const PUBLISHABLE_STAGES = [
  "Listing & Marketing",
  "Sales Confirmation",
  "Receiving Payment",
  "Shipping & Logistics",
  "Confirmation of Receipt",
  "Sale Completion",
];

function effectiveVisibility(item) {
  const mode = item.storefront_publish || "auto";
  if (mode === "force_hidden") return { visible: false, reason: "force_hidden" };
  if (mode === "force_published" || item.published_storefront === true) return { visible: true, reason: "force_published" };
  return { visible: PUBLISHABLE_STAGES.includes(item.stage), reason: "auto" };
}

const stateCopy = ({ visible, reason }) => {
  if (reason === "force_published") return { tone: "brass", text: "force published · live on shop" };
  if (reason === "force_hidden") return { tone: "taupe", text: "force hidden · pulled from shop" };
  return visible
    ? { tone: "brass", text: "auto · visible · piece is at a publishable stage" }
    : { tone: "taupe", text: "auto · hidden · piece is upstream of listing & marketing" };
};

/** Per-item shop visibility control on inventory edit page. */
export default function ShopVisibility({ item, update }) {
  const mode = item.storefront_publish || "auto";
  const { visible, reason } = effectiveVisibility(item);
  const badge = stateCopy({ visible, reason });

  return (
    <section className="bg-linen border border-taupe/50 p-8 mb-10" data-testid="shop-visibility-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl lowercase text-charcoal">storefront visibility</h2>
          <p className="text-xs text-espresso mt-1">decide when this piece is shown on the public shop. auto follows the pipeline; force overrides either way.</p>
        </div>
        <div
          data-testid="visibility-effective-badge"
          className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border flex items-center gap-2 ${
            visible ? "border-brass text-brass" : "border-taupe text-taupe"
          }`}
        >
          {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {badge.text}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {MODES.map((m) => (
          <label
            key={m.value}
            data-testid={`vis-mode-${m.value}`}
            className={`block cursor-pointer border p-5 transition-colors ${
              mode === m.value ? "border-brass bg-ivory" : "border-taupe/40 hover:border-brass/60"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <input
                type="radio"
                name="storefront_publish"
                value={m.value}
                checked={mode === m.value}
                onChange={() => update("storefront_publish", m.value)}
                className="accent-brass"
              />
              <span className="editorial-label" style={{ color: mode === m.value ? "#B08D57" : undefined }}>
                {m.value === "force_published" && <Sparkles className="w-3 h-3 inline mr-1" />}
                {m.label}
              </span>
            </div>
            <p className="text-xs text-espresso leading-relaxed">{m.description}</p>
          </label>
        ))}
      </div>

      {reason === "force_published" && item.published_storefront === true && mode !== "force_published" && (
        <p className="text-xs text-taupe italic mt-4">
          note · legacy <code className="bg-ivory border border-taupe/40 px-1">published_storefront</code> flag is true on this piece, which overrides the auto setting. toggle to &ldquo;force hidden&rdquo; to pull it from the shop.
        </p>
      )}
    </section>
  );
}
